import React, { useState } from 'react';
import { graphql } from 'gatsby';

//@ts-ignore
import * as styles from './statement-block-with-icon-option.module.css';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import {
    getColor,
    getMediaData,
    getMultiChoice,
    getStatement,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils/index';
import {
    KontentRadioMultiChoice,
    KontentStringValue,
    BrandColor,
    CTAConfiguration,
    GeneralImages,
    Statement,
    KontentRichText,
    Statements,
} from '../../../types/KontentProps';
import { RichTextEl } from '../Global/RichText';
import PlainTextElement from '../Global/Headlines';
import AnchorLink from '../Global/AnchorLink';
import CustomHeadline from '../Global/CustomHeadline';
import { useIsClient } from '../Global/DataUtils/useIsClient';
import LazyImage from '../Global/LazyImage';

import Jotform from '../Jotform';

type HeaderTagType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';

import ReactPlayer from '../Global/LazyReactPlayer';

type BgDisplayOptions = 'image' | 'color_only';
type HideOnMobile = 'yes' | 'no';
type PagePositionOptions =
    | 'sits_under_preceding_component'
    | 'lays_on_top_of_preceding_component';
type VideoPositionOptions =
    | 'directly_under_headline'
    | 'under_description_and_cta';

interface Props extends CTAConfiguration, GeneralImages {
    kontentItemId?: string;
    background_display_option: KontentRadioMultiChoice<BgDisplayOptions>;
    component_anchor_name: KontentStringValue;
    content_panel__content_panel_color: BrandColor;
    content_panel__content_panel_font_color: BrandColor;
    description_copy: KontentRichText;
    headline?: KontentStringValue;
    page_positioning: KontentRadioMultiChoice<PagePositionOptions>;
    statements: Statements;
    component_page_background_color: BrandColor;
    hide_icon_statements_on_mobile: KontentRadioMultiChoice<HideOnMobile>;
    video_link?: KontentStringValue;
    video_link_position?: KontentRadioMultiChoice<VideoPositionOptions>;
    autoplay_video?: KontentRadioMultiChoice<HideOnMobile>;
    control_bottom_margin?: KontentRadioMultiChoice<string>;
    desktop_bottom_margin?: KontentStringValue;
    form_link?: KontentStringValue;
    lightbox_vertical_spacing?: KontentStringValue;
    lightbox_horizontal_spacing?: KontentStringValue;
    lightbox_height?: KontentStringValue;
    mobile_bottom_margin?: KontentStringValue;
    headline_style?: KontentRadioMultiChoice<HeaderTagType>;
    headline_tag?: KontentRadioMultiChoice<HeaderTagType>;
    apply_lightbox_height_to_jotform: KontentRadioMultiChoice<HideOnMobile>;
    apply_strong_shadow_to_lightbox: KontentRadioMultiChoice<HideOnMobile>;
    link_entire_component?: KontentRadioMultiChoice<HideOnMobile>;
}

const StatementBlockWithIconOption: React.FC<Props> = ({
    background_display_option,
    component_anchor_name,
    content_panel__content_panel_color,
    content_panel__content_panel_font_color,
    description_copy,
    headline,
    page_positioning,
    statements,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_location_anchor_point,
    cta_configuration__cta_window_action,
    general_large___small_image_selection__large,
    general_large___small_image_selection__small,
    component_page_background_color,
    hide_icon_statements_on_mobile,
    video_link,
    video_link_position,
    autoplay_video,
    control_bottom_margin,
    desktop_bottom_margin,
    mobile_bottom_margin,
    headline_style,
    headline_tag,
    form_link,
    lightbox_height,
    lightbox_vertical_spacing,
    lightbox_horizontal_spacing,
    apply_lightbox_height_to_jotform,
    apply_strong_shadow_to_lightbox,
    link_entire_component,
    kontentItemId,
    ...props
}) => {
    const orange = '#f06623';
    const autoPlayOn = autoplay_video?.value[0]?.codename === 'yes' || false;
    const controlMargin = control_bottom_margin?.value?.[0]?.codename === 'yes';
    const formLink = form_link?.value;
    const applyLightboxHeightToJotform =
        getMultiChoice(apply_lightbox_height_to_jotform) === 'yes' || false;
    const applyStrongShadowToLightbox =
        getMultiChoice(apply_strong_shadow_to_lightbox) === 'yes' || false;
    const linkEntireComponentChoice = getMultiChoice(link_entire_component);
    const linkEntireComponent = linkEntireComponentChoice === 'yes';
    const [showJotform, setShowJotform] = useState(false);

    const breakpoints = useBreakpoint();
    const isClient = useIsClient();

    const LightBoxHeight =
        lightbox_height?.value && isClient && breakpoints.md
            ? lightbox_height.value + 'vh'
            : '83vh';
    const LightBoxVerticalSpacing = lightbox_vertical_spacing?.value
        ? lightbox_vertical_spacing.value + 'vh'
        : '6vh';
    const LightBoxHorizontalSpacing = lightbox_horizontal_spacing?.value
        ? lightbox_horizontal_spacing.value + 'vw'
        : '24px';
    const anchorPointLink = cta_configuration__cta_location_anchor_point?.value;
    const marginVars = controlMargin
        ? ({
              '--sb-desktop-mb': `${desktop_bottom_margin?.value || 0}px`,
              '--sb-mobile-mb': `${mobile_bottom_margin?.value || 0}px`,
          } as React.CSSProperties)
        : {};
    const Video =
        video_link?.value && video_link.value != '' ? (
            <div className={styles.videoContainer}>
                <ReactPlayer
                    url={video_link.value}
                    width="100%"
                    height="100%"
                    playing={autoPlayOn}
                    controls
                />
            </div>
        ) : (
            <></>
        );

    const contentStyles: React.CSSProperties = {
        color: getColor(content_panel__content_panel_font_color),
        backgroundColor: getColor(content_panel__content_panel_color),
        marginTop:
            getMultiChoice(page_positioning) ===
            'lays_on_top_of_preceding_component'
                ? '-8rem'
                : undefined,
    };
    const pageBackgroundColor =
        getColor(component_page_background_color) || '#f5f4f2';
    const largeImage = getMediaData(
        general_large___small_image_selection__large
    );
    const smallImage = getMediaData(
        general_large___small_image_selection__small
    );

    const renderStatement = (statement: Statement, idx: number) => {
        const statementData = getStatement(statement);
        const isTextStyle = statementData.style === 'text';

        return (
            <div
                className={styles.statementCol}
                key={`${idx}${statementData.displayTitle?.value}`}
            >
                <div className={styles.statement}>
                    <div className={styles.iconTitleWrapper}>
                        <div className={styles.iconTitleContent}>
                            {isTextStyle &&
                            statementData.displayTitle?.value ? (
                                <div
                                    className={styles.iconText}
                                    style={{
                                        color:
                                            statementData.displayTitleColor ||
                                            orange,
                                    }}
                                    data-kontent-element-codename="display_title"
                                >
                                    {statementData.displayTitle.value}
                                </div>
                            ) : (
                                statementData?.icon?.url && (
                                    <div
                                        className={styles.iconWrapper}
                                        data-kontent-element-codename="icon"
                                    >
                                        <LazyImage
                                            src={statementData.icon.url}
                                            alt={
                                                statementData.icon
                                                    .description || ''
                                            }
                                            width={90}
                                            height={90}
                                        />
                                    </div>
                                )
                            )}
                            {!isTextStyle && (
                                <h4 data-kontent-element-codename="display_title">
                                    {statementData.displayTitle?.value}
                                </h4>
                            )}
                        </div>
                    </div>

                    {statementData?.richDescription?.value && (
                        <div
                            className={`${styles.statementDescription}${
                                hide_icon_statements_on_mobile?.value[0]
                                    ?.codename !== 'no'
                                    ? ` ${styles.hideOnMobile}`
                                    : ''
                            }`}
                        >
                            <RichTextEl
                                richText={statementData.richDescription}
                                elementCodename="rich_description"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const ctaHref = linkEntireComponent
        ? BuildLinkHref(
              cta_configuration__cta_location,
              cta_configuration__cta_location_anchor_point
          ) || null
        : null;
    const isValidHref = ctaHref !== null && ctaHref.trim() !== '';
    const ctaTarget =
        linkEntireComponent &&
        getMultiChoice(cta_configuration__cta_window_action) === 'new_window'
            ? '_blank'
            : undefined;
    const isClickable = linkEntireComponent && isValidHref;

    const content = (
        <div className={styles.content} style={contentStyles}>
            {headline?.value && (
                <CustomHeadline
                    style={getMultiChoice(headline_style) || 'h2'}
                    tag={getMultiChoice(headline_tag) || 'h2'}
                    headline_value={headline}
                    className={styles.headline}
                    alignment={{ value: [{ codename: 'center' }] }}
                    elementCodename="headline"
                />
            )}

            {getMultiChoice(video_link_position) ===
                'directly_under_headline' && Video}
            {description_copy?.value != '<p><br></p>' && (
                <div className={styles.description}>
                    <RichTextEl
                        richText={description_copy}
                        elementCodename="description_copy"
                    />
                </div>
            )}
            <div
                className={styles.statements}
                id={kontentItemId ? `statements-${kontentItemId}` : undefined}
            >
                {statements?.value && statements.value.length > 0
                    ? statements.value.map((statement, idx) =>
                          renderStatement(statement, idx)
                      )
                    : null}
            </div>
            {formLink && cta_configuration__cta_label?.value ? (
                <>
                    <div className={styles.formButton}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowJotform(true);
                            }}
                            data-kontent-element-codename="cta_configuration__cta_label"
                        >
                            {cta_configuration__cta_label?.value}
                        </button>
                    </div>
                    <Jotform
                        formStyle={'popup'}
                        formUrl={formLink}
                        display={showJotform}
                        onPopUpClose={() => {
                            setShowJotform(false);
                        }}
                        overrideStyle={{
                            height: LightBoxHeight,
                            overflow: 'hidden',
                            overflowY: 'auto',
                            margin: `${
                                isClient && breakpoints.md
                                    ? `${LightBoxVerticalSpacing} ${LightBoxHorizontalSpacing}`
                                    : '2vh 24px'
                            }`,
                            padding: `0`,
                            borderRadius: `1rem`,
                        }}
                        applyLightboxHeightToJotform={
                            applyLightboxHeightToJotform
                        }
                        formHeight={LightBoxHeight}
                        closeButtonSpacing={
                            isClient && breakpoints.md
                                ? LightBoxHorizontalSpacing
                                : ''
                        }
                        applyStrongShadowToLightbox={
                            applyStrongShadowToLightbox
                        }
                    />
                    {showJotform ? (
                        <style>
                            {`
                            body {
                                overflow:hidden;
                            }
                        `}
                        </style>
                    ) : (
                        <></>
                    )}
                </>
            ) : anchorPointLink &&
              cta_configuration__cta_label?.value &&
              !cta_configuration__cta_location?.value?.length ? (
                <div className={styles.ctaWrapper}>
                    <AnchorLink
                        targetId={anchorPointLink}
                        color={
                            getColor(content_panel__content_panel_font_color) ||
                            undefined
                        }
                    >
                        {cta_configuration__cta_label?.value}
                    </AnchorLink>
                </div>
            ) : (
                cta_configuration__cta_location &&
                cta_configuration__cta_label?.value && (
                    <div className={styles.ctaWrapper}>
                        <CtaLink
                            href={BuildLinkHref(
                                cta_configuration__cta_location,
                                cta_configuration__cta_location_anchor_point
                            )}
                            target={
                                getMultiChoice(
                                    cta_configuration__cta_window_action
                                ) === 'same_window'
                                    ? undefined
                                    : '_blank'
                            }
                            color={
                                getColor(
                                    content_panel__content_panel_font_color
                                ) || undefined
                            }
                        >
                            {cta_configuration__cta_label.value}
                        </CtaLink>
                    </div>
                )
            )}
            {getMultiChoice(video_link_position) ===
                'under_description_and_cta' && Video}
            {smallImage?.url && (
                <div data-kontent-element-codename="general_large___small_image_selection__small">
                    <LazyImage
                        src={optimizeKontentImageUrl(smallImage.url, {
                            ...imagePreset.decorative.mobile,
                            q: 95,
                        })}
                        srcSet={buildKontentSrcSet(
                            smallImage.url,
                            imagePreset.decorative.mobile.w,
                            {
                                fm: 'webp' as const,
                                q: 95,
                            }
                        )}
                        sizes="100vw"
                        alt={smallImage?.description || smallImage?.name}
                        className={`${styles.ghostedImage} ${styles.mobileImage}`}
                        {...getDisplayDimensions(
                            smallImage?.width,
                            smallImage?.height,
                            imagePreset.decorative.mobile.w
                        )}
                    />
                </div>
            )}
            {largeImage?.url && (
                <div data-kontent-element-codename="general_large___small_image_selection__large">
                    <LazyImage
                        src={optimizeKontentImageUrl(largeImage.url, {
                            ...imagePreset.decorative.desktop,
                            q: 95,
                        })}
                        srcSet={buildKontentSrcSet(
                            largeImage.url,
                            imagePreset.decorative.desktop.w,
                            {
                                fm: 'webp' as const,
                                q: 95,
                            }
                        )}
                        sizes="(max-width: 767px) 100vw, 50vw"
                        alt={largeImage?.description || largeImage?.name}
                        className={`${styles.ghostedImage} ${styles.desktopImage}`}
                        {...getDisplayDimensions(
                            largeImage?.width,
                            largeImage?.height,
                            imagePreset.decorative.desktop.w
                        )}
                    />
                </div>
            )}
        </div>
    );

    return (
        <section
            className={`${styles.wrapper}${
                controlMargin ? ` ${styles.controlledMargin}` : ''
            }`}
            style={{ backgroundColor: pageBackgroundColor, ...marginVars }}
            id={component_anchor_name.value || undefined}
            data-kontent-item-id={kontentItemId}
        >
            {isClickable ? (
                <a
                    href={ctaHref}
                    target={ctaTarget}
                    rel={
                        ctaTarget === '_blank'
                            ? 'noopener noreferrer'
                            : undefined
                    }
                    className={styles.statementLink}
                    style={{
                        display: 'block',
                        textDecoration: 'none',
                        color: 'inherit',
                    }}
                >
                    {content}
                </a>
            ) : (
                content
            )}
        </section>
    );
};

export default StatementBlockWithIconOption;

export const fragmentStatementBlockWithIconOption = graphql`
    fragment StatementBlockWithIconOption on kontent_item_component___statement_block_with_icon_option {
        id
        elements {
            background_display_option {
                value {
                    codename
                }
            }
            component_anchor_name {
                value
            }
            component_page_background_color {
                ...kontentColor
            }
            content_panel__content_panel_color {
                ...kontentColor
            }
            content_panel__content_panel_font_color {
                ...kontentColor
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            description_copy {
                ...kontentRichText
            }
            general_large___small_image_selection__large {
                ...kontentImage
            }
            general_large___small_image_selection__small {
                ...kontentImage
            }
            headline {
                value
            }
            headline_tag {
                value {
                    name
                    codename
                }
            }
            headline_style {
                value {
                    name
                    codename
                }
            }
            page_positioning {
                value {
                    codename
                }
            }
            hide_icon_statements_on_mobile {
                value {
                    codename
                }
            }
            video_link {
                value
            }
            video_link_position {
                value {
                    codename
                }
            }
            autoplay_video {
                value {
                    codename
                }
            }
            control_bottom_margin {
                value {
                    name
                    codename
                }
            }
            mobile_bottom_margin {
                value
            }
            desktop_bottom_margin {
                value
            }
            form_link {
                value
            }
            lightbox_vertical_spacing {
                value
            }
            lightbox_horizontal_spacing {
                value
            }
            lightbox_height {
                value
            }
            apply_lightbox_height_to_jotform {
                value {
                    codename
                }
            }
            apply_strong_shadow_to_lightbox {
                value {
                    codename
                }
            }
            link_entire_component {
                value {
                    codename
                    name
                }
            }
            statements {
                value {
                    ... on kontent_item_content_item___statement_with_icon {
                        elements {
                            display_title {
                                value
                            }
                            style {
                                value {
                                    codename
                                }
                            }
                            display_title_color {
                                ...kontentColor
                            }
                            icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            ...kontentIcon
                                        }
                                    }
                                }
                            }
                            rich_description {
                                ...kontentRichText
                            }
                        }
                    }
                }
            }
        }
    }
`;
